import { useEffect, useState } from 'react'
import AdminLayout from '../../components/shell/AdminLayout'
import EmptyState from '../../components/shell/EmptyState'
import TeamForm, { type NewTeamValues } from '../../components/admin/TeamForm'
import AssignSupervisorForm from '../../components/admin/AssignSupervisorForm'
import EditTeamForm, { type EditTeamValues } from '../../components/admin/EditTeamForm'
import CloseTeamForm from '../../components/admin/CloseTeamForm'
import { assignTeamSupervisor, createTeam, getAccounts, updateTeam, type Account } from './accountStore'
import { dateLabel, getFieldOffices, getTeams, teamStatus, type FieldOffice, type TeamRow, type TeamStatus } from './teamStore'

type StatusFilter = 'all' | 'open' | 'closed'

// The one form open at a time: creating a team, or acting on one row.
type Panel = { kind: 'create' } | { kind: 'edit' | 'close' | 'assign'; teamId: string }

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'closed', label: 'Closed' },
]

const STATUS_LABEL: Record<TeamStatus, string> = {
  open: 'Open',
  temporary: 'Temporary',
  closing: 'Closes today',
  upcoming: 'Not open yet',
  closed: 'Closed',
}

const STATUS_CLASS: Record<TeamStatus, string> = {
  open: 'bg-success/10 text-success',
  temporary: 'bg-info/10 text-info',
  closing: 'bg-warning/10 text-warning',
  upcoming: 'bg-surface-2 text-ink-muted',
  closed: 'bg-surface-2 text-ink-muted',
}

const ACTION =
  'flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-small font-bold hover:bg-surface-2'

function todayIso(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

/**
 * docs/product/personas.md's Administrator: manage the organizational
 * structure, "teams with open and close dates, and supervisor relationships."
 * Every team with its supervisor and dates, and forms to create one, edit its
 * name and dates, close it, and give it a supervisor. A team is made on its
 * own, without a supervisor.
 */
export default function AdminTeamsPage() {
  const [teams, setTeams] = useState<TeamRow[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [fieldOffices, setFieldOffices] = useState<FieldOffice[]>([])
  const [loaded, setLoaded] = useState(false)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  const [panel, setPanel] = useState<Panel | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([getTeams(), getAccounts(), getFieldOffices()]).then(([loadedTeams, loadedAccounts, offices]) => {
      if (cancelled) return
      setTeams(loadedTeams)
      setAccounts(loadedAccounts)
      setFieldOffices(offices)
      setLoaded(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const supervisorNames = new Map(accounts.map((account) => [account.id, `${account.firstName} ${account.lastName}`]))
  const activeSupervisors = accounts.filter((account) => account.role === 'supervisor' && account.active)
  const panelTeam = panel && panel.kind !== 'create' ? (teams.find((team) => team.id === panel.teamId) ?? null) : null
  const today = todayIso()
  const query = search.trim().toLowerCase()
  const filtered = teams.filter((team) => {
    const status = teamStatus(team, today)
    if (statusFilter === 'open' && status === 'closed') return false
    if (statusFilter === 'closed' && status !== 'closed') return false
    if (!query) return true
    const supervisor = team.supervisorId ? (supervisorNames.get(team.supervisorId) ?? '') : ''
    return `${team.name} ${supervisor}`.toLowerCase().includes(query)
  })

  function openPanel(next: Panel) {
    setNotice(null)
    setFormError(null)
    setPanel(next)
  }

  function closePanel() {
    setPanel(null)
    setFormError(null)
  }

  async function save(values: NewTeamValues) {
    setSaving(true)
    setFormError(null)
    // With one field office the function chooses it; with several the form
    // names which.
    const result = await createTeam(values.name, values.fieldOfficeId, { openDate: values.openDate, closeDate: values.closeDate })
    setSaving(false)
    if (!result.ok) {
      setFormError(result.message)
      return
    }
    setTeams(await getTeams())
    setPanel(null)
    setNotice(`Created ${values.name}.`)
  }

  async function assign(supervisorId: string) {
    if (!panelTeam) return
    setSaving(true)
    setFormError(null)
    const result = await assignTeamSupervisor(panelTeam.id, supervisorId)
    setSaving(false)
    if (!result.ok) {
      setFormError(result.message)
      return
    }
    setTeams(await getTeams())
    setPanel(null)
    setNotice(`${supervisorNames.get(supervisorId) ?? 'The supervisor'} now leads ${panelTeam.name}.`)
  }

  // Editing and closing are the same change, a team's name and dates; closing
  // is the shortcut that sets only the close date.
  async function change(values: { name: string; openDate: string; closeDate: string | null }, done: string) {
    if (!panelTeam) return
    setSaving(true)
    setFormError(null)
    const result = await updateTeam(panelTeam.id, values)
    setSaving(false)
    if (!result.ok) {
      setFormError(result.message)
      return
    }
    setTeams(await getTeams())
    setPanel(null)
    setNotice(done)
  }

  function saveEdit(values: EditTeamValues) {
    return change(values, `Updated ${values.name}.`)
  }

  function closeTeam(closeDate: string) {
    if (!panelTeam) return Promise.resolve()
    return change({ name: panelTeam.name, openDate: panelTeam.openDate, closeDate }, `Closed ${panelTeam.name}.`)
  }

  return (
    <AdminLayout current="teams" title="Field offices & teams" subtitle="Create, edit, and close teams, and choose who leads each">
      <div className="flex flex-wrap items-center gap-3 rounded-surface border border-line bg-canvas p-3">
        <label htmlFor="team-search" className="sr-only">
          Search teams
        </label>
        <input
          id="team-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by team or supervisor"
          className="min-h-11 min-w-[240px] flex-1 rounded-control border border-line-strong px-3 text-small"
        />
        <div role="group" aria-label="Filter by status" className="flex gap-2">
          {STATUS_FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={statusFilter === option.key}
              onClick={() => setStatusFilter(option.key)}
              className={`flex min-h-11 items-center justify-center rounded-control border px-3 text-small font-bold ${
                statusFilter === option.key ? 'border-primary bg-selected' : 'border-line-strong hover:bg-surface-2'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-expanded={panel?.kind === 'create'}
          onClick={() => openPanel({ kind: 'create' })}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
        >
          Create team
        </button>
      </div>

      {notice && (
        <p role="status" className="text-small font-semibold text-success">
          {notice}
        </p>
      )}

      {panel?.kind === 'create' && (
        <TeamForm today={today} fieldOffices={fieldOffices} saving={saving} error={formError} onSave={save} onCancel={closePanel} />
      )}

      {panel?.kind === 'edit' && panelTeam && (
        <EditTeamForm key={panelTeam.id} team={panelTeam} saving={saving} error={formError} onSave={saveEdit} onCancel={closePanel} />
      )}

      {panel?.kind === 'close' && panelTeam && (
        <CloseTeamForm key={panelTeam.id} team={panelTeam} today={today} saving={saving} error={formError} onSave={closeTeam} onCancel={closePanel} />
      )}

      {panel?.kind === 'assign' && panelTeam && (
        <AssignSupervisorForm
          key={panelTeam.id}
          teamName={panelTeam.name}
          currentSupervisor={accounts.find((account) => account.id === panelTeam.supervisorId) ?? null}
          supervisors={activeSupervisors}
          saving={saving}
          error={formError}
          onSave={assign}
          onCancel={closePanel}
        />
      )}

      {!loaded ? (
        <p role="status" className="text-small text-ink-muted">
          Loading&hellip;
        </p>
      ) : filtered.length === 0 ? (
        <EmptyState>No teams match your search.</EmptyState>
      ) : (
        // A table wider than the screen scrolls sideways; nothing in it takes
        // focus, so the region itself does, and a keyboard can scroll it.
        <div
          role="region"
          aria-label="Teams"
          tabIndex={0}
          className="overflow-x-auto rounded-surface border border-line focus-visible:outline-2 focus-visible:outline-primary"
        >
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {['Team', 'Supervisor', 'Status', 'Opens', 'Closes', 'Field office'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="border-b border-line bg-surface-2 px-3 py-2 text-left text-caption font-bold tracking-wide whitespace-nowrap text-ink-subtle uppercase"
                  >
                    {heading}
                  </th>
                ))}
                <th scope="col" className="border-b border-line bg-surface-2 py-2 pr-2 pl-3 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((team) => {
                const status = teamStatus(team, today)
                const supervisor = team.supervisorId ? supervisorNames.get(team.supervisorId) : undefined
                return (
                  <tr key={team.id}>
                    <th scope="row" className="border-b border-line px-3 py-2 text-left text-small font-bold">
                      {team.name}
                    </th>
                    <td className="border-b border-line px-3 py-2 text-small">
                      {supervisor ?? <span className="text-ink-muted">No supervisor yet</span>}
                    </td>
                    <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap">
                      <span className={`rounded-full px-2 py-0.5 text-caption font-bold ${STATUS_CLASS[status]}`}>{STATUS_LABEL[status]}</span>
                    </td>
                    <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap text-ink-muted">{dateLabel(team.openDate)}</td>
                    <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap text-ink-muted">
                      {team.closeDate ? dateLabel(team.closeDate) : ''}
                    </td>
                    <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap text-ink-muted">{team.fieldOffice}</td>
                    <td className="border-b border-line py-2 pr-2 pl-3 text-small">
                      {/* Each button has its own fixed slot, kept even when the
                          button is absent (a closed team), so Edit, Close, and
                          the supervisor button line up in every row. */}
                      <div className="flex justify-end gap-2 whitespace-nowrap">
                        <div className="w-14">
                          <button
                            type="button"
                            aria-label={`Edit ${team.name}`}
                            onClick={() => openPanel({ kind: 'edit', teamId: team.id })}
                            className={`${ACTION} w-full`}
                          >
                            Edit
                          </button>
                        </div>
                        <div className="w-16">
                          {status !== 'closed' && (
                            <button
                              type="button"
                              aria-label={`Close ${team.name}`}
                              onClick={() => openPanel({ kind: 'close', teamId: team.id })}
                              className={`${ACTION} w-full`}
                            >
                              Close
                            </button>
                          )}
                        </div>
                        <div className="w-40">
                          {status !== 'closed' && (
                            <button
                              type="button"
                              aria-label={`${team.supervisorId ? 'Change' : 'Assign'} the supervisor of ${team.name}`}
                              onClick={() => openPanel({ kind: 'assign', teamId: team.id })}
                              className={`${ACTION} w-full`}
                            >
                              {team.supervisorId ? 'Change supervisor' : 'Assign supervisor'}
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  )
}
